'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSetState } from 'src/hooks/use-set-state';

import axios, { endpoints } from 'src/utils/axios';

import { AuthContext } from '../auth-context';
import { forceRefreshToken } from './action';
import { REFRESH_TOKEN, STORAGE_KEY, Token, USER } from './constant';
import { isValidToken, setSession } from './utils';

import { mutate } from 'swr';
import type { AuthState } from '../../types';
import { useNewRelicUser } from 'src/hooks/user-newrelic-user';
import { resetAnalyticsSession, syncAnalyticsUser } from 'src/lib/analytics';

// ----------------------------------------------------------------------

/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { i18n } = useTranslation();
  const { state, setState } = useSetState<AuthState>({
    user: null,
    loading: true,
  });

  useNewRelicUser(state.user?.id);

  const prevUserRef = useRef(state.user);

  useEffect(() => {
    if (state.user) {
      syncAnalyticsUser(state.user);
    } else if (prevUserRef.current) {
      resetAnalyticsSession();
    }
    prevUserRef.current = state.user;
  }, [state.user]);

  const checkUserSession = useCallback(async () => {
    try {
      // Get data from localStorage
      const accessToken = localStorage.getItem(Token);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);
      const userFromStorageString = localStorage.getItem(USER);
      let userFromStorage;

      if (userFromStorageString) {
        try {
          userFromStorage = JSON.parse(userFromStorageString);
        } catch {
          userFromStorage = undefined;
        }
      }

      if (accessToken && isValidToken(accessToken)) {
        await setSession(accessToken, refreshToken, userFromStorage);

        const res = await axios.get(endpoints.auth.me, { reauthenticate: false });

        const { user } = res.data;

        setState({ user: { ...user, accessToken }, loading: false });

        localStorage.setItem(USER, JSON.stringify(user));

        // Language is managed by i18next via localStorage (i18nextLng).
        // The language switcher saves to localStorage and i18next restores it on refresh.
        // We no longer override it here to avoid resetting the user's local choice.
      } else if (refreshToken) {
        // Try to refresh the token if we have a refresh token but no valid access token
        try {
          const updatedUser = await forceRefreshToken();
          setState({
            user: { ...updatedUser, accessToken: localStorage.getItem('token') },
            loading: false,
          });

          // Language is managed by i18next via localStorage (i18nextLng).
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          setState({ user: null, loading: false });
        }
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  const refreshUserToken = useCallback(async () => {
    try {
      setState({ loading: true });
      const updatedUser = await forceRefreshToken();
      setState({
        user: { ...updatedUser, accessToken: localStorage.getItem(Token) },
        loading: false,
      });
      return updatedUser;
    } catch (error) {
      console.error('Error refreshing user token:', error);
      setState({ loading: false });
      throw error;
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = useCallback(async () => {
    try {
      await axios.post(endpoints.auth.logout, undefined, { reauthenticate: false });
    } catch {
      // Always clear local session even if revoke fails (expired token, offline).
    }
    resetAnalyticsSession();
    localStorage.removeItem(Token);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(USER);
    mutate(() => true, undefined, { revalidate: false });
    setState({ user: null, loading: false });
  }, [setState]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            id: state.user.id,
            role: state.user?.role ?? 'admin',
          }
        : null,
      checkUserSession,
      refreshUserToken,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      signOut,
    }),
    [checkUserSession, refreshUserToken, state.user, status, signOut]
  );

  useEffect(() => {
    // 🔄 Sync logout across tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== Token || event.newValue !== null) {
        return;
      }
      if (localStorage.getItem(Token)) {
        return;
      }
      signOut();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [signOut]);

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
